import { useEffect, useRef } from 'react';
import TCClient from 'client';
import { useAppDispatch } from 'redux-stores';
import {
  MESSAGE_IDS_CHUNK_SIZE,
  conversationTasksKey,
  toCounterPartyType,
  useFetchConversationTasksMutation,
} from 'redux-stores/tasks/tasksApi';
import { tasksSlice } from 'redux-stores/tasks/tasksSlice';

const { setActiveConversationKey } = tasksSlice.actions;

type UseConversationTasksArgs = {
  enabled: boolean;
  organizationId?: string;
  counterPartyId?: string;
  /** Conversation `counterPartyType` ('user' | 'group' | 'distributionList' | ...). */
  counterPartyType?: string;
  /** Ids of the messages currently loaded in the timeline. */
  messageIds: string[];
};

/**
 * Loads the current user's tasks for the open conversation into
 * `tasks.conversationTasksByKey`.
 * - Lazy loading on: one request for the whole conversation (no message_ids).
 * - Lazy loading off: requests the loaded message ids (≤200 per request) and,
 *   as the timeline grows, only the ids not requested yet.
 */
export function useConversationTasks({
  enabled,
  organizationId,
  counterPartyId,
  counterPartyType,
  messageIds,
}: UseConversationTasksArgs): void {
  const dispatch = useAppDispatch();
  const [fetchConversationTasks] = useFetchConversationTasksMutation();
  const requestedIdsRef = useRef<Set<string>>(new Set());
  const lazyLoading = !!(TCClient as unknown as { config?: { lazyLoading?: boolean } }).config
    ?.lazyLoading;
  const type = toCounterPartyType(counterPartyType);
  const isSupported = enabled && !!organizationId && !!counterPartyId && !!type;
  // Callers pass a fresh array each render; key on content so failed ids retry only when the
  // timeline actually changes, not on every re-render.
  const idsKey = messageIds.join(',');

  useEffect(() => {
    requestedIdsRef.current = new Set();
    if (!isSupported || !organizationId || !counterPartyId || !type) return;
    const target = { organizationId, counterPartyId, counterPartyType: type };
    dispatch(setActiveConversationKey(conversationTasksKey(target)));
    if (lazyLoading) fetchConversationTasks(target);
    return () => {
      dispatch(setActiveConversationKey(null));
    };
  }, [
    isSupported,
    organizationId,
    counterPartyId,
    type,
    lazyLoading,
    dispatch,
    fetchConversationTasks,
  ]);

  useEffect(() => {
    if (!isSupported || !organizationId || !counterPartyId || !type || lazyLoading) return;
    const newIds = idsKey.split(',').filter((id) => id && !requestedIdsRef.current.has(id));
    newIds.forEach((id) => requestedIdsRef.current.add(id));
    const requestedIds = requestedIdsRef.current;
    for (let i = 0; i < newIds.length; i += MESSAGE_IDS_CHUNK_SIZE) {
      const chunk = newIds.slice(i, i + MESSAGE_IDS_CHUNK_SIZE);
      fetchConversationTasks({
        organizationId,
        counterPartyId,
        counterPartyType: type,
        messageIds: chunk,
      })
        .unwrap()
        // Un-mark failed ids so the next timeline change retries them.
        .catch(() => chunk.forEach((id) => requestedIds.delete(id)));
    }
  }, [
    isSupported,
    organizationId,
    counterPartyId,
    type,
    lazyLoading,
    idsKey,
    fetchConversationTasks,
  ]);
}
